import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import apiRequest from "services/axois";
import AlertServices from "services/alertServices";
import permissionServices from "services/checkPermission";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash } from "react-icons/fa";

/**
 * Keep component OUTSIDE to avoid focus drops due to component recreation.
 */
const PasswordInput = ({
  t,
  label,
  id,
  name,
  value,
  onChange,
  show,
  onToggle,
  autoComplete,
  inputRef,
}) => {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      <div className="relative mt-1">
        <input
          ref={inputRef}
          id={id}
          name={name}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          required
          autoComplete={autoComplete}
          className="block w-full pr-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        />

        <button
          type="button"
          // stops icon click from stealing input focus
          onMouseDown={(e) => e.preventDefault()}
          onClick={onToggle}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-800"
          aria-label={show ? t("Hide password") : t("Show password")}
          title={show ? t("Hide password") : t("Show password")}
          tabIndex={-1}
        >
          {show ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>
    </div>
  );
};

const ChangePasswordFirst = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");

  // Controlled values (prevents wiping on toggle)
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  // show/hide toggles
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // refs to keep focus stable
  const oldRef = useRef(null);
  const newRef = useRef(null);
  const confirmRef = useRef(null);

  useEffect(() => {
    const storedToken = permissionServices.getUserType();
    if (!storedToken) navigate("/");
  }, [navigate]);

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 8)
      errors.push(t("Password must be more than 8 characters long."));
    if (!/[a-z]/.test(password))
      errors.push(t("Password must contain at least one lowercase letter."));
    if (!/[0-9]/.test(password))
      errors.push(t("Password must contain at least one number."));

    if (errors.length > 0) {
      setPasswordErrorMessage(errors[0]);
      setPasswordError(true);
      return true; // failed
    }

    setPasswordError(false);
    setPasswordErrorMessage("");
    return false; // passed
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      toast.error(t("New passwords do not match!"));
      return;
    }

    if (oldPassword === newPassword) {
      toast.error(t("New password cannot be the same as the old password!"));
      return;
    }

    if (validatePassword(newPassword)) return;

    try {
      const response = await apiRequest("post", "/auth/reset-password", {
        oldPassword,
        newPassword,
      });

      if (response?.data?.statusCode === 200) {
        AlertServices.AlertMessage(
          t("Password changed successfully!"),
          "success"
        );
        navigate("/");
      } else if (response?.data?.statusCode === 401) {
        AlertServices.AlertMessage(t("Password is incorrect"), "error");
      } else {
        AlertServices.AlertMessage(t("Please try again"), "error");
      }
    } catch (error) {
      toast.error(t("Error changing password. Please try again."));
      console.error("Change password error:", error);
    }
  };

  // Toggle handlers with refocus (keeps cursor in the same field)
  const toggleOld = () => {
    setShowOld((v) => !v);
    requestAnimationFrame(() => oldRef.current?.focus());
  };
  const toggleNew = () => {
    setShowNew((v) => !v);
    requestAnimationFrame(() => newRef.current?.focus());
  };
  const toggleConfirm = () => {
    setShowConfirm((v) => !v);
    requestAnimationFrame(() => confirmRef.current?.focus());
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 px-4">
      <div className="bg-white shadow-lg rounded-lg p-8 max-w-sm w-full">
        <h2 className="text-xl font-bold text-center mb-6">
          {t("Change Password")}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordInput
            t={t}
            label={t("Old Password")}
            id="oldPassword"
            name="oldPassword"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            show={showOld}
            onToggle={toggleOld}
            autoComplete="current-password"
            inputRef={oldRef}
          />

          <PasswordInput
            t={t}
            label={t("New Password")}
            id="newPassword"
            name="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            show={showNew}
            onToggle={toggleNew}
            autoComplete="new-password"
            inputRef={newRef}
          />

          <PasswordInput
            t={t}
            label={t("Confirm New Password")}
            id="confirmNewPassword"
            name="confirmNewPassword"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            show={showConfirm}
            onToggle={toggleConfirm}
            autoComplete="new-password"
            inputRef={confirmRef}
          />

          {passwordError && (
            <div className="text-sm text-red-500">{passwordErrorMessage}</div>
          )}

          <button
            type="submit"
            className="w-full bg-primary text-white py-2 px-4 rounded-md shadow hover:bg-primaryHover transition duration-150"
          >
            {t("Change Password")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordFirst;
